import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  Sparkles,
  ChevronRight,
  Cake,
  Heart,
  Briefcase,
  MapPin,
  Rocket,
  GraduationCap,
  MessageCircle,
  Copy,
  Check,
} from 'lucide-react'
import { useState } from 'react'
import { cn } from '@/lib/utils'
import { useFollowUpSuggestions } from '@/hooks/use-follow-up-suggestions'
import type { FollowUpSuggestion } from '@/lib/types'

const priorityStyles = {
  high: 'border-l-red-500',
  medium: 'border-l-amber-500',
  low: 'border-l-blue-400',
}

const lifeEventIcons: Record<string, typeof Briefcase> = {
  career: Briefcase,
  relationship: Heart,
  family: Heart,
  relocation: MapPin,
  launch: Rocket,
  education: GraduationCap,
  birthday: Cake,
  health: Heart,
  retirement: Briefcase,
}

function SuggestionCard({ suggestion }: { suggestion: FollowUpSuggestion }) {
  const { t } = useTranslation()
  const [copied, setCopied] = useState(false)

  const initials = suggestion.contactName
    .split(' ')
    .map((w) => w[0])
    .join('')
    .toUpperCase()
    .slice(0, 2)

  const handleCopyMessage = () => {
    if (!suggestion.suggestedMessage) return
    navigator.clipboard.writeText(suggestion.suggestedMessage)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  return (
    <div
      className={cn(
        'bg-surface-container-low overflow-hidden rounded-xl border-l-4',
        priorityStyles[suggestion.priority],
      )}
    >
      <Link
        to={`/contacts/${suggestion.contactId}`}
        className="hover:bg-surface-container-high flex items-start gap-3 p-4 transition-colors"
      >
        {suggestion.avatarUrl ? (
          <img
            src={suggestion.avatarUrl}
            alt={suggestion.contactName}
            className="h-10 w-10 shrink-0 rounded-full object-cover"
          />
        ) : (
          <div className="bg-primary/10 text-primary flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-sm font-bold">
            {initials || '?'}
          </div>
        )}
        <div className="min-w-0 flex-1">
          <div className="flex items-center justify-between">
            <p className="text-sm font-semibold">{suggestion.contactName}</p>
            <ChevronRight className="text-outline h-4 w-4 shrink-0" />
          </div>
          <p className="text-on-surface-variant mt-0.5 text-xs">{suggestion.reason}</p>

          {/* Life events badges */}
          {suggestion.lifeEvents.length > 0 && (
            <div className="mt-1.5 flex flex-wrap gap-1">
              {suggestion.lifeEvents.map((event) => {
                const Icon = lifeEventIcons[event.type] ?? Sparkles
                return (
                  <span
                    key={event.type}
                    className="bg-ring/10 text-ring inline-flex items-center gap-0.5 rounded-full px-2 py-0.5 text-[10px] font-medium"
                  >
                    <Icon className="h-2.5 w-2.5" />
                    {event.label}
                  </span>
                )
              })}
            </div>
          )}

          {/* Upcoming dates badges */}
          {suggestion.upcomingDates.length > 0 && (
            <div className="mt-1 flex flex-wrap gap-1">
              {suggestion.upcomingDates.map((d) => (
                <span
                  key={d.type}
                  className="tag-lilac inline-flex items-center gap-0.5 rounded-full px-2 py-0.5 text-[10px] font-medium"
                >
                  <Cake className="h-2.5 w-2.5" />
                  {t('smartFollowUp.daysUntil', '{{type}} in {{count}}d', {
                    type: d.type,
                    count: d.daysUntil,
                  })}
                </span>
              ))}
            </div>
          )}

          {/* AI suggested action */}
          {suggestion.suggestedAction && (
            <div className="mt-2 flex items-start gap-1.5">
              <MessageCircle className="text-primary mt-0.5 h-3 w-3 shrink-0" />
              <p className="text-primary text-xs font-medium">{suggestion.suggestedAction}</p>
            </div>
          )}
        </div>
      </Link>

      {/* AI suggested message - copy to clipboard */}
      {suggestion.suggestedMessage && (
        <div className="border-outline-variant/10 border-t px-4 py-2.5">
          <div className="flex items-start gap-2">
            <p className="text-on-surface-variant flex-1 text-xs italic">
              &ldquo;{suggestion.suggestedMessage}&rdquo;
            </p>
            <button
              onClick={handleCopyMessage}
              className="text-on-surface-variant hover:text-primary shrink-0 p-1 transition-colors"
              aria-label={t('smartFollowUp.copyMessage', 'Copy message')}
            >
              {copied ? (
                <Check className="bg-chartreuse text-chartreuse-foreground h-3.5 w-3.5 rounded-full p-0.5" />
              ) : (
                <Copy className="h-3.5 w-3.5" />
              )}
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

export function FollowUpSuggestionsWidget() {
  const { t } = useTranslation()
  const { data, isLoading } = useFollowUpSuggestions({ limit: 5, ai: false })

  if (isLoading) {
    return (
      <section className="space-y-3">
        <div className="flex items-center gap-2">
          <Sparkles className="text-primary h-4 w-4" />
          <h2 className="font-heading text-base font-bold">
            {t('smartFollowUp.title', 'Smart Follow-ups')}
          </h2>
        </div>
        {Array.from({ length: 3 }).map((_, i) => (
          <div key={i} className="bg-surface-container-low h-20 animate-pulse rounded-xl" />
        ))}
      </section>
    )
  }

  const suggestions = data?.suggestions ?? []

  if (suggestions.length === 0) return null

  return (
    <section className="space-y-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Sparkles className="text-primary h-4 w-4" />
          <h2 className="font-heading text-base font-bold">
            {t('smartFollowUp.title', 'Smart Follow-ups')}
          </h2>
        </div>
        {data && data.total > 5 && (
          <span className="text-on-surface-variant text-xs">
            {t('smartFollowUp.more', '+{{count}} more', { count: data.total - 5 })}
          </span>
        )}
      </div>
      <div className="space-y-3">
        {suggestions.map((suggestion) => (
          <SuggestionCard key={suggestion.contactId} suggestion={suggestion} />
        ))}
      </div>
    </section>
  )
}
